import type { BombiixFillBatchItem } from '../../../../../../Components/BombiixAGGrid/types';
import { isValidGradingInput } from '../../../../../../utils/calculationHelpers';
import type { MeasurementGridRow } from './grid-types';
import { isSizeRendered } from './size-ranges';

/** Column field prefix of the size columns; the size name follows it. */
export const SIZE_FIELD_PREFIX = 'size:';

/** Which of a size cell's two stacked inputs a value belongs to. */
export type SizeFillKind = 'measurment' | 'grading';

/** The row-change field a fill writes (`code`, `description`, `tolMinus`, `measurment:<size>`, `grading:<size>`) and its value. */
export interface MeasurementFillWrite {
  field: string;
  value: string;
}

const toDisplay = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

/**
 * Which of a size cell's inputs is editable on this row, mirroring SizeCell: the measurement on the
 * anchor (sample or zero) size, the grading on every other size while show-grading is on, and
 * neither on a row without a POM item or for a size the product does not show.
 * @param row - the grid row
 * @param sizeName - the size column
 * @returns the editable input, or null when the cell takes no input
 */
export function sizeFillKind(row: MeasurementGridRow, sizeName: string): SizeFillKind | null {
  if (!row.hasPom || !isSizeRendered(row.productSizeRanges ?? [], row.hiddenSizeNames ?? [], sizeName)) return null;
  const grading = (row.gradings ?? []).find((entry) => entry.sizeName === sizeName);
  const hasIsZeroKey = grading ? Object.prototype.hasOwnProperty.call(grading, 'isZero') : false;
  const hasIsSampleKey = grading ? Object.prototype.hasOwnProperty.call(grading, 'isSampleSize') : false;
  const measurmentDisabled = (hasIsZeroKey && !grading?.isZero) || (hasIsSampleKey && !grading?.isSampleSize);
  if (!measurmentDisabled) return 'measurment';
  const gradingDisabled = Boolean(grading?.isZero || grading?.isSampleSize);
  return row.activeGrading && !gradingDisabled ? 'grading' : null;
}

/**
 * The value a size cell's editable input shows, which is what a fill copies; '' when nothing is editable.
 * @param row - the grid row
 * @param sizeName - the size column
 * @returns the measurement or grading text
 */
export function sizeFillValue(row: MeasurementGridRow, sizeName: string): string {
  const kind = sizeFillKind(row, sizeName);
  if (!kind) return '';
  const grading = (row.gradings ?? []).find((entry) => entry.sizeName === sizeName);
  if (kind === 'measurment') return toDisplay(grading?.measurment);
  const text = toDisplay(grading?.grading);
  return text === '' ? '0' : text;
}

/**
 * A size column's cell value, preferring what a fill or paste has just written onto the row.
 * AG Grid writes the dropped value onto `row[field]` and reads it back through this getter; the row
 * is rebuilt from state once the write is dispatched, which drops the key again.
 * @param row - the grid row, absent on a group row
 * @param sizeName - the size column
 * @returns the pending fill value, or the editable input's value
 */
export function sizeCellValue(row: MeasurementGridRow | undefined, sizeName: string): string | null {
  if (!row) return '';
  const key = `${SIZE_FIELD_PREFIX}${sizeName}`;
  if (Object.prototype.hasOwnProperty.call(row, key)) return row[key] as string | null;
  return sizeFillValue(row, sizeName);
}

/**
 * The write one filled or pasted cell produces, or null when the cell's own input would refuse it.
 * Text cells take any text on a POM row. A size cell takes a valid grading number, and only into the
 * input the drag started from, so a measurement dragged from an anchor size never lands as a grading.
 * With several source rows, the source is the one whose value at that size is the value dropped.
 * @param item - the cell AG Grid wrote
 * @param sourceRows - the rows the drag started from, empty for a paste
 * @returns the row-change field and value, or null to skip the cell
 */
export function buildMeasurementFillWrite(item: BombiixFillBatchItem<MeasurementGridRow>, sourceRows: MeasurementGridRow[]): MeasurementFillWrite | null {
  const row = item.data;
  const value = toDisplay(item.newValue);
  if (!row.hasPom) return null;
  if (TEXT_FILL_FIELDS.includes(item.field)) return { field: item.field, value };
  if (!item.field.startsWith(SIZE_FIELD_PREFIX)) return null;
  const sizeName = item.field.slice(SIZE_FIELD_PREFIX.length);
  const kind = sizeFillKind(row, sizeName);
  if (!kind) return null;
  const sources = sourceRows.length > 1 ? sourceRows.filter((source) => sizeFillValue(source, sizeName) === value) : sourceRows;
  if (sourceRows.length && !sources.some((source) => sizeFillKind(source, sizeName) === kind)) return null;
  if (value !== '' && !isValidGradingInput(value)) return null;
  return { field: `${kind}:${sizeName}`, value };
}

/** The text columns a fill writes as typed: POM code, POM description and tolerance. */
const TEXT_FILL_FIELDS: ReadonlyArray<string> = ['code', 'description', 'tolMinus'];
